import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Check, Loader2, XCircle } from 'lucide-react';

export default function SuscripcionExito() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState('verificando');

  const paymentId = params.get('payment_id');
  const mpStatus = params.get('status');
  const externalRef = params.get('external_reference');
  const preapprovalId = params.get('preapproval_id');
  const stripeSessionId = params.get('session_id');

  const verificar = useCallback(async (intentos = 0) => {
    const MAX_INTENTOS = 5;
    const INTERVALO_MS = 2000;

    // Stripe flow — poll until paid or max attempts
    if (stripeSessionId) {
      try {
        const res = await api.get(`/pagos/stripe/checkout-status/${stripeSessionId}`);
        if (res.data.payment_status === 'paid') {
          setEstado('exitoso');
          return;
        } else if (res.data.status === 'expired') {
          setEstado('error');
          return;
        } else if (intentos < MAX_INTENTOS) {
          setTimeout(() => verificar(intentos + 1), INTERVALO_MS);
          return;
        } else {
          // Assume success after max polling (webhook will handle activation)
          setEstado('exitoso');
        }
      } catch {
        if (intentos < MAX_INTENTOS) {
          setTimeout(() => verificar(intentos + 1), INTERVALO_MS);
          return;
        }
        setEstado('exitoso');
      }
      return;
    }
    // MercadoPago flow
    if (preapprovalId || mpStatus === 'approved' || paymentId) {
      try {
        const qp = new URLSearchParams();
        if (paymentId) qp.set('payment_id', paymentId);
        if (mpStatus) qp.set('status', mpStatus);
        if (externalRef) qp.set('external_reference', externalRef);
        if (preapprovalId) qp.set('preapproval_id', preapprovalId);
        const res = await api.get(`/pagos/verificar-pago?${qp.toString()}`);
        if (res.data.payment_status === 'approved' || res.data.payment_status === 'already_activated') {
          setEstado('exitoso');
        } else if (res.data.payment_status === 'pending' || res.data.payment_status === 'in_process') {
          setEstado('pendiente');
        } else {
          setEstado('exitoso');
        }
      } catch {
        setEstado('exitoso');
      }
    } else if (mpStatus === 'pending' || mpStatus === 'in_process') {
      setEstado('pendiente');
    } else if (mpStatus === 'rejected') {
      setEstado('error');
    } else {
      setEstado('exitoso');
    }
  }, [paymentId, mpStatus, externalRef, stripeSessionId, preapprovalId]);

  useEffect(() => {
    verificar();
  }, [verificar]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="suscripcion-exito-page">
      <div className="max-w-md w-full text-center">
        {estado === 'verificando' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-6" />
            <h1 className="font-barlow text-2xl font-bold text-slate-50 uppercase mb-3">Verificando pago...</h1>
            <p className="text-slate-400">Esto toma unos segundos.</p>
          </>
        )}
        {estado === 'exitoso' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-400" strokeWidth={2.5} />
            </div>
            <h1 className="font-barlow text-3xl font-bold text-slate-50 uppercase mb-3">Pago Exitoso</h1>
            <p className="text-slate-400 mb-8">Tu suscripcion ha sido activada por 30 dias. Ya puedes usar todas las funciones.</p>
            <Button onClick={() => { window.location.href = '/login'; }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm px-8 h-11"
              data-testid="ir-al-panel-btn">
              Ir a Mi Panel
            </Button>
          </>
        )}
        {estado === 'pendiente' && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="font-barlow text-2xl font-bold text-amber-400 uppercase mb-3">Pago Pendiente</h1>
            <p className="text-slate-400 mb-6">Tu pago esta en proceso. Te notificaremos cuando se confirme. Esto puede tomar unos minutos.</p>
            <Button onClick={() => { window.location.href = '/login'; }} variant="outline"
              className="border-slate-700 text-slate-300"
              data-testid="volver-panel-btn">
              Volver a Mi Panel
            </Button>
          </>
        )}
        {estado === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-barlow text-2xl font-bold text-red-400 uppercase mb-3">Pago Rechazado</h1>
            <p className="text-slate-400 mb-6">No pudimos procesar tu pago. Intenta con otro metodo de pago.</p>
            <Button onClick={() => navigate('/suscripcion')} variant="outline"
              className="border-slate-700 text-slate-300"
              data-testid="reintentar-pago-btn">
              Reintentar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
